import { Card, Space, Typography } from 'antd';
import React, { createContext, useState, useEffect } from 'react';
import './widget.css';
import Icon from '@mdi/react';
import { mdiPencil, mdiFloppy } from '@mdi/js';
import WidgetFilter from './WidgetFilter';
import clsx from 'clsx';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import NinjaTableCustomize from 'components/tables/NinjaTable/NinjaTableCustomize';
import { useDashboardLayout } from 'store/redux/states/dashboard/dashboard.hooks';
import PropTypes from 'prop-types';
import { getDateRangeByKey } from 'components/DateRangePicker/components/staticRanges';

export const WidgetContext = createContext({});

const Widget = (props) => {
  const {
    widget_key,
    title,
    index,
    tableWidget,
    disableLevelFilter,
    disableLimit,
    disableDateFilter,
    disableStatusFilter,
    disableAcountsFilter,
    movingButton,
    deleteWidget = false,
    disableShopifyFilter,
    disableAccountsFilter,
    icon,
    default_filter,
    ...card
  } = props;

  const [runFilterTimestamp, setRunFilterTimestamp] = useState(new Date().getTime());
  const { config, updateWidget, isLoading } = useDashboardLayout();
  const { date_from, date_to } = useWidgetDate(widget_key);
  const [filters, setFilters] = useState({
    ...default_filter,
    date_from: default_filter?.date_from || date_from,
    date_to: default_filter?.date_to || date_to,
  });

  const { Paragraph } = Typography;
  const [editableStr, setEditableStr] = useState(title);

  useEffect(() => {
    if (date_from && date_to) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        date_from,
        date_to,
      }));
    }
  }, [date_from, date_to]);

  useEffect(() => {
    if (default_filter?.date_range_key) {
      const df = getDateRangeByKey(default_filter.date_range_key);
      setFilters((prevFilters) => ({
        ...prevFilters,
        date_from: df.date_from,
        date_to: df.date_to,
      }));
    } else if (default_filter?.date_from && default_filter?.date_to) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        date_from: default_filter.date_from,
        date_to: default_filter.date_to,
      }));
    }
  }, [default_filter]);

  const changeWidgetTitle = (newTitle) => {
    if (newTitle !== title) {
      const updatedConfig = [...config];
      updatedConfig[index] = {
        ...updatedConfig[index],
        title: newTitle,
      };
      updateWidget(updatedConfig);
    }
  };

  const handleDelete = () => {
    const updatedConfig = config.filter((_, i) => i !== index);
    updateWidget(updatedConfig);
  };

  /** Update filter timestamp, table and query listens to it and then it is updated */
  const runFilter = () => {
    setRunFilterTimestamp(new Date().getTime());
  };

  /** Change reporting level */
  const changeLevel = (level) => {
    setFilters((f) => ({
      ...f,
      level,
    }));
    runFilter();
    props.onLevelChange?.(level);
  };

  const changeLimit = (value) => {
    setFilters((f) => ({
      ...f,
      limit: value,
    }));
    runFilter();
  };

  const options = {
    adset: {
      name: 'Adsets',
      value: 'adset',
    },
    campaign: {
      name: 'Campaigns',
      value: 'campaign',
    },
    ad: {
      name: 'Ads',
      value: 'ad',
    },
  };

  return (
    <WidgetContext.Provider
      value={{
        widget_key,
        title,
        filters,
        setFilters,
        runFilterTimestamp,
        runFilter,
        default_filter,
        widget_filters: {
          ...filters,
        },
      }}
    >
      <Card
        title={
          <div className='flex items-center'>
            {icon}
            <Paragraph
              className='ml-4 !my-0 !p-0 !h-fit !w-fit flex items-center gap-2'
              editable={{
                icon: <Icon path={mdiPencil} className='p-px text-color-light hover:text-orange' />,
                tooltip: 'Edit Title',
                onChange: (newTitle) => {
                  setEditableStr(newTitle);
                  changeWidgetTitle(newTitle);
                },
                text: editableStr,
                enterIcon: <Icon path={mdiFloppy} className='!text-orange !cursor=-pointer' />,
              }}
            >
              {editableStr}
            </Paragraph>
          </div>
        }
        extra={
          <Space>
            {movingButton}
            <NinjaTableCustomize
              changeLimit={changeLimit}
              changeLevel={changeLevel}
              disableLevelFilter={disableLevelFilter}
              levelOption={options}
              defaultLevel={filters?.level || 'adset'}
              defaultPerPage={filters?.limit || 10}
              disableLimit={disableLimit}
              deleteWidget={deleteWidget}
              handleDelete={handleDelete}
              index={index}
              deleteLoading={isLoading}
              renderFilter={
                <WidgetFilter
                  index={index}
                  widget_key={widget_key}
                  disableDateFilter={disableDateFilter}
                  disableAccountsFilter={disableAccountsFilter}
                  disableStatusFilter={disableStatusFilter}
                  disableShopifyFilter={disableShopifyFilter}
                  disableAcountsFilter={disableAcountsFilter}
                  default_filter={default_filter}
                />
              }
            />
          </Space>
        }
        className={clsx('widget-card w-full', {
          'table-widget': tableWidget,
        })}
        {...card}
      >
        {props.children}
      </Card>
    </WidgetContext.Provider>
  );
};

export default Widget;

Widget.propTypes = {
  widget_key: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  disableLevelFilter: PropTypes.bool,
  disableLimit: PropTypes.bool,
  tableWidget: PropTypes.bool,
  disableDateFilter: PropTypes.bool,
  disableStatusFilter: PropTypes.bool,
  disableAcountsFilter: PropTypes.bool,
  movingButton: PropTypes.func,
  deleteWidget: PropTypes.bool,
  disableShopifyFilter: PropTypes.bool,
  disableAccountsFilter: PropTypes.bool,
  data: PropTypes.object,
  icon: PropTypes.element,
  default_filter: PropTypes.object,
  onLevelChange: PropTypes.func,
  children: PropTypes.node,
};
